import React, { useState, useEffect } from 'react';
import { BeakerIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { isEmailTestMode, setEmailTestMode, getTestEmailAddress, getTestEmailAddresses } from '../../services/emailService';

interface EmailTestModeToggleProps {
  className?: string;
}

/**
 * Email Test Mode Toggle Component
 * When enabled, all emails are redirected to the test address
 * instead of the actual recipients.
 */
export const EmailTestModeToggle: React.FC<EmailTestModeToggleProps> = ({ className = '' }) => {
  const [enabled, setEnabled] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [testAddress, setTestAddress] = useState('nroland89@gmail.com');

  useEffect(() => {
    setEnabled(isEmailTestMode());
    setTestAddress(getTestEmailAddress());
  }, []);

  const handleToggle = () => {
    const newValue = !enabled;
    setEmailTestMode(newValue);
    setEnabled(newValue);
    setTestAddress(getTestEmailAddress());
    
    if (newValue) {
      setShowWarning(true);
      setTimeout(() => setShowWarning(false), 5000);
    }
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleToggle}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
            enabled ? 'bg-amber-500' : 'bg-gray-200'
          }`}
          title={enabled ? 'Teszt mód aktív - minden email hozzád jön' : 'Teszt mód kikapcsolva'}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
        <div className="flex items-center gap-1.5">
          <BeakerIcon className={`w-4 h-4 ${enabled ? 'text-amber-600' : 'text-gray-400'}`} />
          <span className={`text-sm font-medium ${enabled ? 'text-amber-700' : 'text-gray-500'}`}>
            {enabled ? 'Teszt mód BE' : 'Teszt mód KI'}
          </span>
        </div>
      </div>
      
      {enabled && (
        <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
          <span className="font-semibold">Figyelem:</span> Minden email BCC-vel megy:
          <div className="mt-1 flex flex-col gap-0.5">
            {getTestEmailAddresses().map((addr) => (
              <span key={addr} className="font-mono font-semibold">• {addr}</span>
            ))}
          </div>
        </div>
      )}
      
      {showWarning && (
        <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded animate-pulse">
          <ExclamationTriangleIcon className="w-3.5 h-3.5" />
          <span>Teszt mód aktiválva! Az email-ek nem az ügyfeleknek mennek.</span>
        </div>
      )}
    </div>
  );
};

export default EmailTestModeToggle;
